import { supabase, getUid } from "./supabase";
import { enqueue, removeOps } from "./outbox";
import { cacheGet, cacheSet } from "./dataCache";

/* Upsert a shoe row. Quietly no-ops if signed out; quietly retries next
   round if the network hiccups (upserts are idempotent by shoe id). */
// Raw upsert (network only) — used directly by the offline outbox flush so a
// replay never re-enqueues. Payload already carries user_id + updated_at.
export async function upsertShoeRaw(payload) {
  const { error } = await supabase.from("shoes").upsert(payload);
  if (error) console.warn("shoe sync failed:", error.message);
  return !error;
}

export async function upsertShoe(row) {
  const uid = await getUid();
  if (!uid) return false;
  const payload = { ...row, user_id: uid, updated_at: new Date().toISOString() };
  const ok = await upsertShoeRaw(payload);
  if (ok) {
    // Direct write succeeded → drop any stale queued op for this shoe so a
    // later flush can't replay older state over the row.
    removeOps("shoe", payload.id);
  } else {
    // Offline / failure → queue the latest state (coalesced by shoe id) for replay.
    enqueue({ kind: "shoe", key: payload.id, payload });
  }
  return ok;
}

/* Ask the server to replay-validate a completed shoe. The verdict lands on
   the row's validated / validation_error columns, which only the server
   can write.

   Raw variant (network only) — used by the outbox flush so a replay never
   re-enqueues. Any 2xx response is success, including an "invalid" verdict:
   the request was delivered and the verdict is final. Only transport/server
   errors return null. */
export async function requestValidationRaw(shoeId) {
  const { data, error } = await supabase.functions.invoke("validate-shoe", {
    body: { shoeId },
  });
  if (error) {
    console.warn("validation request failed:", error.message);
    return null;
  }
  return data;
}

/* Called only after a successful ONLINE completion save (the trainer gates it
   on `saved`), so queuing a retry here cannot re-open the offline best-of-N
   hole that OFFLINE-PWA-DESIGN closes by never validating outbox-replayed
   shoes. A transient 500 at the moment a ranked shoe completes used to
   permanently forfeit its ranking; now the request is coalesced into the
   outbox (kind "validate", keyed by shoe id) and retried with the usual
   dead-letter cap. A seed that expires before a retry lands just yields a
   terminal "expired" verdict, which counts as delivered and self-cleans. */
export async function requestValidation(shoeId) {
  const data = await requestValidationRaw(shoeId);
  if (data == null) enqueue({ kind: "validate", key: shoeId, payload: { shoeId } });
  return data;
}

const SHOE_LIST_COLS =
  "id, created_at, engine_version, seed, system, decks, settings, ok, total, net, cc_ok, cc_total, complete, validated, validation_error, ranked, mp_session_id";

// Shared list query for one user's shoes. Omits the heavy jsonb blobs `events`
// and `state` (never rendered in the list) and `decisions` (lazy-loaded per shoe
// via fetchShoeDecisions). The (user_id, created_at desc) index serves it.
async function shoeRowsFor(userId, limit) {
  const { data, error } = await supabase
    .from("shoes")
    .select(SHOE_LIST_COLS)
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) {
    console.warn("shoe fetch failed:", error.message);
    return null; // error ≠ empty: callers show "couldn't load", not "no shoes yet"
  }
  return data ?? [];
}

// The signed-in user's own shoes. MUST filter by user_id: with the Friends
// "read friends visible shoes" RLS policy live, an unfiltered select would also
// return friends' visible shoes and pollute the owner's Review.
export async function fetchShoes(limit = 100) {
  const uid = await getUid();
  if (!uid) return [];
  return shoeRowsFor(uid, limit);
}

// A friend's shoes (Friends → profile). RLS returns only the rows the owner
// shares with accepted, non-blocked friends, honoring their per-type flags.
export async function fetchUserShoes(userId, limit = 100) {
  return shoeRowsFor(userId, limit);
}

/* Fetch one shoe's `decisions` array on demand (Review expands the per-shoe
   detail). Kept out of the list query so the heavy array ships only when a
   user actually opens a shoe. PK lookup, RLS-scoped to the owner. */
export async function fetchShoeDecisions(id) {
  const { data, error } = await supabase
    .from("shoes")
    .select("decisions, count_checks")
    .eq("id", id)
    .single();
  if (error) {
    console.warn("decisions fetch failed:", error.message);
    return { decisions: [], countChecks: [] };
  }
  return { decisions: data?.decisions ?? [], countChecks: data?.count_checks ?? [] };
}

export async function deleteShoe(id) {
  const { error } = await supabase.from("shoes").delete().eq("id", id);
  if (error) console.warn("shoe delete failed:", error.message);
  return !error;
}

/* Most recent in-progress shoe with a resumable snapshot — but only if it
   was touched within the last hour. Older partials are skipped on purpose:
   the player no longer holds the running count, so resuming would corrupt
   the training (count-blind decisions graded against a count they lost).
   Stale partials simply remain as history in Review. */
const FRESHNESS_MS = 60 * 60 * 1000;

export async function fetchActiveShoe() {
  // MUST filter by user_id. The Friends "read friends' visible shoes" RLS policy
  // means an unfiltered select can return another user's in-progress shoe — so
  // resume would surface (and load) someone else's shoe. Scope to the caller.
  const uid = await getUid();
  if (!uid) return null;
  const cutoff = new Date(Date.now() - FRESHNESS_MS).toISOString();
  const { data, error } = await supabase
    .from("shoes")
    .select("*")
    .eq("user_id", uid)
    .eq("complete", false)
    .not("state", "is", null)
    .gte("updated_at", cutoff)
    .order("updated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) {
    console.warn("active shoe fetch failed:", error.message);
    return null;
  }
  return data;
}

/* "Leave shoe": keep the partial's history but drop its resume snapshot
   so it stops prompting. */
export async function clearShoeState(id) {
  const { error } = await supabase
    .from("shoes")
    .update({ state: null, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) console.warn("clear shoe state failed:", error.message);
}

/* Minimal per-shoe rows for the Progress/Trends view (Pro). Only the numeric
   columns the charts need (no heavy jsonb), own-rows, complete shoes, oldest
   first so the client can bucket them into a time series. */
const TREND_COLS =
  "created_at, ok, total, net, hands, cc_ok, cc_total, ranked, validated, system, bc_n, bc_sx, bc_sy, bc_sxx, bc_syy, bc_sxy";
export async function fetchShoeTrends(limit = 5000) {
  const uid = await getUid();
  if (!uid) return [];
  // Newest-first + limit keeps the LATEST 5000 shoes (ascending kept the
  // oldest, so a heavy player's new shoes silently stopped appearing in
  // Progress/curriculum — CODE-REVIEW logic #12); reversed back to
  // chronological order for the chart bucketing and recency evals.
  const { data: rows, error: e2 } = await supabase
    .from("shoes")
    .select(TREND_COLS)
    .eq("user_id", uid)
    .eq("complete", true)
    .order("created_at", { ascending: false })
    .limit(limit);
  const key = `trends:${uid}`;
  if (e2) {
    console.warn("trends fetch failed:", e2.message);
    return cacheGet(key) ?? null; // offline → last-seen; never-cached error → null (≠ empty)
  }
  const chron = (rows ?? []).reverse();
  cacheSet(key, chron);
  return chron;
}
